"use client";

import { CaretLeft, TrendDown, TrendUp } from "@repo/ui/components/icons";
import { cn } from "@repo/ui/lib/utils";
import Link from "next/link";
import { useId, useState } from "react";

import { getPriceChart, toPriceSeries, type PriceRange, type PriceSeries } from "../../../../lib/api-client";

// The fill under the line fades top to bottom from `fill` to `fillEnd`, and
// the whole area is drawn at 10%, so it stays a faint wash (F141).
const METAL_STYLE = {
  gold: { line: "stroke-chart-gold", fill: "var(--color-chart-gold-fill)", fillEnd: "var(--color-chart-gold-fill-end)", dot: "bg-chart-gold" },
  silver: { line: "stroke-muted-foreground", fill: "var(--color-muted-foreground)", fillEnd: "var(--color-muted-foreground)", dot: "bg-muted-foreground" },
} as const;

const fmt = (n: number) => n.toLocaleString("fa-IR");

const RANGES: { range: PriceRange; label: string }[] = [
  { range: "day", label: "امروز" },
  { range: "week", label: "هفته" },
  { range: "month", label: "ماه" },
];

// Gold and silver in one compact card (F141, F142, amended 2026-10-18):
// - Phones: a light navy card (tint-navy), all of it a link to /price holding
//   only the two small charts side by side, each with its latest price small
//   and centred under it; no names or buttons: the line's colour says which
//   metal it is (gold, silver gray). Always the month range.
// - From sm up: each metal with its change and price over a shorter chart;
//   the range (امروز / هفته / ماه, month first) as small chips in the
//   card's header, beside a small «قیمت لحظه‌ای» link. Other ranges are
//   fetched on first pick.
export function PriceChartCard({ series: monthSeries }: { series: PriceSeries[] }) {
  const [range, setRange] = useState<PriceRange>("month");
  const [byRange, setByRange] = useState<Partial<Record<PriceRange, PriceSeries[]>>>({ month: monthSeries });
  const series = byRange[range];

  function pick(next: PriceRange) {
    setRange(next);
    // Loaded ranges are kept; one that came back empty (api down) is retried.
    if (byRange[next]?.some((s) => s.values.length)) return;
    void getPriceChart(next).then(({ data }) => {
      const loaded = toPriceSeries(data);
      setByRange((prev) => ({ ...prev, [next]: loaded }));
    });
  }

  // While a range loads, the previous one stays drawn, dimmed.
  const shown = series ?? byRange.month!;

  return (
    <>
      <Link
        href="/price"
        aria-label="قیمت لحظه‌ای طلا و نقره"
        className="bg-tint-navy grid grid-cols-2 gap-3 rounded-card p-3 shadow-sm transition-opacity active:opacity-80 sm:hidden"
      >
        {monthSeries.map((s) => (
          <div key={s.metal} className="flex min-w-0 flex-col items-center gap-1">
            <Sparkline series={s} />
            {/* The latest price, as small as it stays legible. */}
            {s.values.length ? (
              <p className="text-caption leading-none font-semibold tabular-nums">
                {fmt(s.values[s.values.length - 1]!)}
                <span className="text-muted-foreground font-normal"> تومان/{s.unitLabel}</span>
              </p>
            ) : null}
          </div>
        ))}
      </Link>

      <section aria-label="قیمت طلا و نقره" className="bg-background hidden h-full flex-col gap-3 rounded-card p-4 shadow-sm sm:flex sm:p-5">
        <div className="flex items-center justify-between gap-3">
          <div role="radiogroup" aria-label="بازه نمودار" className="flex gap-1.5">
            {RANGES.map((r) => (
              <button
                key={r.range}
                type="button"
                role="radio"
                aria-checked={range === r.range}
                onClick={() => pick(r.range)}
                className={cn(
                  "rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors",
                  range === r.range ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground",
                )}
              >
                {r.label}
              </button>
            ))}
          </div>
          <Link href="/price" className="text-muted-foreground hover:text-foreground flex items-center gap-0.5 text-xs font-medium">
            قیمت لحظه‌ای
            <CaretLeft size={14} />
          </Link>
        </div>
        <div className={cn("grid grid-cols-2 transition-opacity", !series && "opacity-50")} aria-busy={!series}>
          {shown.map((s, i) => (
            <div key={s.metal} className={cn("min-w-0", i === 0 ? "pe-5" : "border-border/50 border-s ps-5")}>
              <MetalChart series={s} />
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

/** Phones: the line and its faint fill only, in the metal's colour. */
function Sparkline({ series }: { series: PriceSeries }) {
  const gradientId = useId();
  const style = METAL_STYLE[series.metal];
  const { values } = series;
  if (values.length < 2) return <div className="bg-muted h-14 rounded-xl" aria-hidden />;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const pad = (max - min) * 0.2 || 1;
  const x = (i: number) => (i / (values.length - 1)) * 100;
  const y = (v: number) => 100 - ((v - (min - pad)) / (max + pad - (min - pad))) * 100;
  const line = values.map((v, i) => `${i ? "L" : "M"}${x(i)},${y(v)}`).join(" ");
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="h-14 w-full" role="img" aria-label={series.title}>
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={style.fill} stopOpacity={1} />
          <stop offset="1" stopColor={style.fillEnd} stopOpacity={0} />
        </linearGradient>
      </defs>
      <path d={`${line} L100,100 L0,100 Z`} fill={`url(#${gradientId})`} fillOpacity={0.1} />
      <path d={line} fill="none" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" className={style.line} />
    </svg>
  );
}

// One metal over the chosen range (F141, F142): name + change over the
// range, headline price, then a single-series line (2px, straight segments
// between hourly or daily closes, a faint fading fill under it) with a dot, crosshair and tooltip on
// hover/touch. Time runs left→right (oldest first) as in finance
// apps, even on an RTL page. The SVG stretches to the panel (non-scaling
// strokes); the dot, crosshair and tooltip are HTML on top so they stay round
// and crisp.
function MetalChart({ series }: { series: PriceSeries }) {
  const [hover, setHover] = useState<number | null>(null);
  const gradientId = useId();
  const { values, labels: dayLabels } = series;
  const style = METAL_STYLE[series.metal];

  // No history yet (or api down): say so rather than draw a made-up line.
  if (values.length < 2) {
    return (
      <div className="flex flex-col gap-2">
        <h2 className="text-sm font-bold">{series.title}</h2>
        <p className="text-muted-foreground text-sm">{values.length === 1 ? `${fmt(values[0]!)} تومان/${series.unitLabel}` : "قیمت در دسترس نیست"}</p>
      </div>
    );
  }

  const last = values[values.length - 1]!;
  const first = values[0]!;
  const changePct = ((last - first) / first) * 100;
  const up = changePct >= 0;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const pad = (max - min) * 0.2 || 1;
  const x = (i: number) => (i / (values.length - 1)) * 100;
  const y = (v: number) => 100 - ((v - (min - pad)) / (max + pad - (min - pad))) * 100;

  const line = values.map((v, i) => `${i ? "L" : "M"}${x(i)},${y(v)}`).join(" ");
  const area = `${line} L100,100 L0,100 Z`;

  return (
    <div className="flex h-full flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-bold">{series.title}</h2>
        <span
          className={cn(
            "flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
            up ? "bg-tint-green text-ink-green" : "bg-tint-red text-ink-red",
          )}
        >
          {up ? <TrendUp size={14} /> : <TrendDown size={14} />}
          <span dir="ltr">
            {up ? "+" : "−"}
            {Math.abs(changePct).toLocaleString("fa-IR", { maximumFractionDigits: 1 })}٪
          </span>
        </span>
      </div>

      <p className="text-lg font-extrabold sm:text-xl">
        {fmt(last)}
        <span className="text-muted-foreground text-xs font-normal"> تومان/{series.unitLabel}</span>
      </p>

      <div dir="ltr" className="mt-auto">
        <div
          className="relative h-14 touch-pan-y sm:h-16"
          onPointerMove={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const frac = (e.clientX - rect.left) / rect.width;
            setHover(Math.max(0, Math.min(values.length - 1, Math.round(frac * (values.length - 1)))));
          }}
          onPointerLeave={() => setHover(null)}
        >
          <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full" aria-hidden>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor={style.fill} stopOpacity={1} />
                <stop offset="1" stopColor={style.fillEnd} stopOpacity={0} />
              </linearGradient>
            </defs>
            <path d={area} fill={`url(#${gradientId})`} fillOpacity={0.1} />
            <path
              d={line}
              fill="none"
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
              className={style.line}
            />
          </svg>

          {hover !== null ? <div className="bg-border pointer-events-none absolute inset-y-0 w-px" style={{ left: `${x(hover)}%` }} /> : null}

          {hover !== null ? (
            <span
              className={cn(
                "ring-background pointer-events-none absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2",
                style.dot,
              )}
              style={{ left: `${x(hover)}%`, top: `${y(values[hover]!)}%` }}
            />
          ) : null}

          {hover !== null ? (
            <div
              dir="rtl"
              className="bg-foreground text-background pointer-events-none absolute -top-2 z-10 rounded-md px-2 py-1 text-xs whitespace-nowrap shadow-sm"
              style={{
                left: `${x(hover)}%`,
                transform: `translate(${hover === 0 ? "0" : hover === values.length - 1 ? "-100%" : "-50%"}, -100%)`,
              }}
            >
              {dayLabels[hover]}: {fmt(values[hover]!)} تومان
            </div>
          ) : null}
        </div>
      </div>

      {/* sr-only on the wrapper, not the table: Firefox and Safari draw a
          table's caption outside a clipped table box, which printed the
          metal's name a second time over the chart. */}
      <div className="sr-only">
        <table>
          <caption>{series.title}</caption>
          <tbody>
            {values.map((v, i) => (
              <tr key={i}>
                <th scope="row">{dayLabels[i]}</th>
                <td>{fmt(v)} تومان</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
